import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTitle, Meta, Display, Chip, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, daysSince, taskTypeMeta } from '../lib/api';
import { formatLocalDate } from '../i18n';

/**
 * Calendar - kalendarz sezonu z prawdziwymi taskami z konta zalogowanego.
 *
 * Z 04-component-composition.md ekran 4:
 * - Skala toggle: Tydzień (default) | Miesiąc — Sezon i Lata wyłączone (disabled)
 * - Sekcja DZIŚ na górze (highlight)
 * - Następne 7 dni
 * - Sticky karencja warning na desktop
 * - Faza księżyca + zimni ogrodnicy w sidebar (desktop only)
 *
 * Endpoints:
 *   /api/tasks?limit=100 - wszystkie taski usera (filter frontend-side po dacie)
 */

const TONE_COLOR = {
  sage:  'var(--t-sage)',
  rust:  'var(--t-rust)',
  amber: 'var(--t-amber)',
  dim:   'var(--t-text-mute)',
};

const ICONS = {
  sprout: Icons.Sprout,
  scissors: Icons.Scissors,
  drop: Icons.Drop,
  sun: Icons.Sun,
};

// ── Helpers ──────────────────────────────────────────────────────
function isoDate(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function weekNumber(date) {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  return 1 + Math.ceil((firstThursday - target) / 604800000);
}

function groupTasksByDate(tasks) {
  const map = new Map();
  tasks.forEach(t => {
    if (!t.due_date || t.completed) return;
    const key = t.due_date.slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  });
  return map;
}

// ── Week strip - 7 dni na pasie ──────────────────────────────────
function WeekStrip({ days, todayIso, tasksByDate, onPick, picked }) {
  const dayLabels = ['P', 'W', 'Ś', 'C', 'P', 'S', 'N'];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
      gap: 'clamp(3px, 1vw, 6px)',
    }}>
      {days.map((d, i) => {
        const dIso = isoDate(d);
        const isToday = dIso === todayIso;
        const isPicked = dIso === picked;
        const has = (tasksByDate.get(dIso) || []).length > 0;
        return (
          <div key={i} onClick={() => onPick(dIso)} style={{
            padding: '12px 0 14px', borderRadius: 10,
            background: isToday ? 'var(--t-sage)' : isPicked ? 'rgba(168, 196, 158, 0.12)' : 'var(--t-bg-elev)',
            color: isToday ? 'var(--t-bg)' : 'var(--t-text)',
            border: `1px solid ${isToday ? 'var(--t-sage)' : isPicked ? 'var(--t-sage)' : 'var(--t-line)'}`,
            textAlign: 'center', cursor: 'pointer',
          }}>
            <div style={{
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.14em', opacity: 0.7, marginBottom: 4,
            }}>{dayLabels[i]}</div>
            <Display size={20} style={{ color: 'inherit' }}>{d.getDate()}</Display>
            {has && (
              <div style={{
                width: 4, height: 4, borderRadius: '50%',
                background: isToday ? 'var(--t-bg)' : 'var(--t-amber)',
                margin: '6px auto 0',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Event line ──────────────────────────────────────────────────
function EventLine({ task }) {
  const meta = taskTypeMeta(task.task_type);
  const Icon = ICONS[meta.iconName] || Icons.Sun;
  const color = TONE_COLOR[meta.tone] || TONE_COLOR.sage;
  const ghost = false;

  return (
    <div style={{
      display: 'flex', gap: 14, padding: '10px 0',
      opacity: ghost ? 0.5 : 1,
    }}>
      <div style={{
        width: 36, flexShrink: 0,
        display: 'grid', placeItems: 'center',
        marginTop: 2,
      }}>
        <Icon color={color} size={14} />
      </div>
      <div style={{ width: 2, background: color, borderRadius: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--t-text)', letterSpacing: '-.01em' }}>
          {task.description}
        </div>
        {(task.plant_name || task.plot_name) && (
          <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 2 }}>
            {[task.plot_name, task.plant_name].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Day section ──────────────────────────────────────────────────
function DaySection({ dateIso, tasks, isToday }) {
  const { t } = useTranslation();
  const date = new Date(dateIso);
  const formatted = formatLocalDate(dateIso, 'weekday');

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 10, paddingBottom: 8,
        borderBottom: `1px solid ${isToday ? 'var(--t-sage)' : 'var(--t-line)'}`,
      }}>
        <Display size={18} style={{ color: isToday ? 'var(--t-sage)' : 'var(--t-text)' }}>
          {isToday ? `${t('common.today')} — ${formatted}` : formatted}
        </Display>
        <Meta>
          {tasks.length === 0 ? t('calendar.no_events') : `${tasks.length} zad.`}
        </Meta>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tasks.length === 0 ? (
          <div style={{
            padding: '8px 0', fontSize: 13,
            color: 'var(--t-text-mute)', fontStyle: 'italic',
          }}>↳ {t('calendar.no_events_hint')}</div>
        ) : tasks.map(task => <EventLine key={task.id} task={task} />)}
      </div>
    </div>
  );
}

// ── Sidebar (desktop) ────────────────────────────────────────────
function CalendarSidebar({ scale, setScale }) {
  const { t } = useTranslation();
  const scales = [
    { k: 'week',   l: t('calendar.scale_week') },
    { k: 'month',  l: t('calendar.scale_month') },
    { k: 'season', l: t('calendar.scale_season'), disabled: true },
    { k: 'years',  l: t('calendar.scale_years'),  disabled: true },
  ];
  return (
    <div>
      <Meta style={{ marginBottom: 10 }}>{t('calendar.title')}</Meta>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 6, marginBottom: 24,
      }}>
        {scales.map(s => (
          <button
            key={s.k}
            onClick={() => !s.disabled && setScale(s.k)}
            disabled={s.disabled}
            style={{
              padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${scale === s.k ? 'var(--t-sage)' : 'var(--t-line)'}`,
              background: scale === s.k ? 'rgba(168, 196, 158, 0.1)' : 'var(--t-bg-elev)',
              color: s.disabled ? 'var(--t-text-mute)' : 'var(--t-text)',
              fontFamily: 'var(--t-sans)', fontSize: 12,
              cursor: s.disabled ? 'not-allowed' : 'pointer',
              opacity: s.disabled ? 0.5 : 1,
            }}
          >{s.l}</button>
        ))}
      </div>

      <MoonPhaseCard />
      <IceSaintsCard />
    </div>
  );
}

// ── Faza księżyca live z /api/calendar/moon/current ─────────────
function MoonPhaseCard() {
  const { t } = useTranslation();
  const { data, loading } = useApi('/api/calendar/moon/current');

  if (loading) {
    return (
      <div style={{
        padding: '16px 18px', borderRadius: 14,
        background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
        marginBottom: 16, minHeight: 90,
      }}>
        <Meta style={{ marginBottom: 10 }}>{t('calendar.moon_phase_label')}</Meta>
      </div>
    );
  }

  if (!data?.moon) return null;

  const { moon, gardening } = data;
  // Pierwsza rekomendacja "favorable" jako tagline
  const tagline = gardening?.favorable?.[0] || gardening?.general || '';

  return (
    <Link to="/pogoda" style={{ textDecoration: 'none' }}>
      <div style={{
        padding: '16px 18px', borderRadius: 14,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        marginBottom: 16,
        cursor: 'pointer',
        transition: 'border-color 200ms ease',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--t-sage)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--t-line)'}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 10,
        }}>
          <Meta>{t('calendar.moon_phase_label')}</Meta>
          <span style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.16em', color: 'var(--t-text-mute)',
          }}>{Math.round(moon.illumination)}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{moon.emoji}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Display size={16} style={{ display: 'block' }}>{moon.phaseName}</Display>
            {tagline && (
              <div style={{
                fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3,
                lineHeight: 1.4,
              }}>{tagline}</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Zimni ogrodnicy — pokazuj tylko w sezonie wiosennym ───────
function IceSaintsCard() {
  const { t } = useTranslation();
  const month = new Date().getMonth() + 1;
  // Pokaż od kwietnia (przygotowanie) do połowy maja
  if (month < 4 || month > 5) return null;

  const today = new Date();
  const iceSaintsStart = new Date(today.getFullYear(), 4, 12); // 12 maja
  const iceSaintsEnd = new Date(today.getFullYear(), 4, 15);   // 15 maja
  const daysToStart = Math.ceil((iceSaintsStart - today) / 86400000);
  const isAfterIceSaints = today > iceSaintsEnd;

  if (isAfterIceSaints) return null;

  const subtitle = daysToStart > 0
    ? `Za ${daysToStart} ${daysToStart === 1 ? 'dzień' : 'dni'} — czekaj z wrażliwymi`
    : 'Trwa okres ryzyka — wrażliwe rośliny pod osłoną';

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 14,
      background: 'rgba(192, 106, 74, 0.08)',
      borderLeft: '3px solid var(--t-rust)',
    }}>
      <Meta color="rust" style={{ marginBottom: 6 }}>{t('calendar.ice_saints_label')}</Meta>
      <Display size={16} style={{ display: 'block', marginBottom: 4 }}>12-15 maja</Display>
      <div style={{ fontSize: 11, color: 'var(--t-text-dim)', lineHeight: 1.4 }}>
        {subtitle}: pomidor, papryka, ogórek, cukinia — pod osłoną do 15.05.
      </div>
    </div>
  );
}

// ── Modal eventów dnia (klik dnia w MonthGrid) ─────────────────
function DayEventsModal({ open, onClose, dateIso, tasks }) {
  if (!open) return null;
  const date = dateIso ? new Date(dateIso) : new Date();

  return (
    <div
      role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15, 20, 16, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line)',
          borderRadius: 16,
          maxWidth: 560, width: '100%',
          maxHeight: '85dvh',
          overflow: 'auto',
          padding: 24,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 20, gap: 14,
        }}>
          <div>
            <Meta color="sage" style={{ marginBottom: 4 }}>
              {formatLocalDate(date, 'long')}
            </Meta>
            <Display size={24} style={{ display: 'block', letterSpacing: '-.02em' }}>
              {tasks.length === 0 ? 'Wolny dzień' : `${tasks.length} ${tasks.length === 1 ? 'zadanie' : tasks.length < 5 ? 'zadania' : 'zadań'}`}
            </Display>
          </div>
          <button
            type="button" onClick={onClose}
            aria-label="Zamknij"
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--t-text-mute)', cursor: 'pointer',
              fontSize: 22, lineHeight: 1, padding: 0,
              width: 44, height: 44, margin: '-10px -10px 0 0',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >×</button>
        </div>
        {tasks.length === 0 ? (
          <div style={{
            padding: '24px', textAlign: 'center',
            color: 'var(--t-text-mute)', fontSize: 13,
            fontStyle: 'italic',
          }}>↳ W tym dniu nic nie masz zaplanowanego.</div>
        ) : (
          <div>
            {tasks.map(task => (
              <EventLine key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MonthGrid (7×6) - widok miesiąca ──────────────────────────
function MonthGrid({ year, month, tasksByDate, todayIso, onPickDay }) {
  // Pierwszy dzień miesiąca (od poniedziałku)
  const firstOfMonth = new Date(year, month, 1);
  const startWeek = startOfWeekMonday(firstOfMonth);
  // Generuj 42 dni (6 tygodni × 7 dni)
  const days = [];
  for (let i = 0; i < 42; i++) {
    days.push(addDays(startWeek, i));
  }
  const dayLabels = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 'clamp(2px, 0.5vw, 4px)',
        marginBottom: 6,
      }}>
        {dayLabels.map(d => (
          <div key={d} style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)',
            textAlign: 'center', padding: '8px 0',
          }}>{d}</div>
        ))}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 'clamp(2px, 0.5vw, 4px)',
      }}>
        {days.map(d => {
          const dIso = isoDate(d);
          const dayTasks = tasksByDate.get(dIso) || [];
          const inMonth = d.getMonth() === month;
          const isToday = dIso === todayIso;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;

          // Oznacz tony eventów (max 3 kropki)
          const tones = [...new Set(dayTasks.slice(0, 6).map(t => taskTypeMeta(t.task_type).tone))].slice(0, 3);

          return (
            <button
              key={dIso}
              type="button"
              onClick={() => onPickDay(dIso)}
              style={{
                aspectRatio: '1 / 1',
                padding: 6, borderRadius: 8,
                background: isToday
                  ? 'rgba(168, 196, 158, 0.10)'
                  : 'var(--t-bg-elev)',
                border: `1px solid ${isToday ? 'var(--t-sage)' : 'var(--t-line)'}`,
                cursor: 'pointer',
                opacity: inMonth ? 1 : 0.35,
                display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between', alignItems: 'flex-start',
                color: 'var(--t-text)',
                fontFamily: 'var(--t-sans)', fontSize: 14,
                position: 'relative',
                transition: 'border-color 200ms ease, background 200ms ease',
              }}
              onMouseEnter={e => {
                if (isToday) return;
                e.currentTarget.style.borderColor = 'var(--t-line-2)';
                e.currentTarget.style.background = 'rgba(232, 230, 221, 0.04)';
              }}
              onMouseLeave={e => {
                if (isToday) return;
                e.currentTarget.style.borderColor = 'var(--t-line)';
                e.currentTarget.style.background = 'var(--t-bg-elev)';
              }}
            >
              <span style={{
                fontWeight: isToday ? 600 : 400,
                color: isToday ? 'var(--t-sage)' :
                       isWeekend && inMonth ? 'var(--t-text-dim)' :
                       'var(--t-text)',
                letterSpacing: '-.01em',
              }}>{d.getDate()}</span>
              {tones.length > 0 && (
                <div style={{
                  display: 'flex', gap: 3, marginTop: 'auto',
                }}>
                  {tones.map((tone, i) => (
                    <div key={i} style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: TONE_COLOR[tone] || TONE_COLOR.sage,
                    }} />
                  ))}
                  {dayTasks.length > tones.length && (
                    <span style={{
                      fontSize: 8, color: 'var(--t-text-mute)',
                      fontFamily: 'var(--t-mono)', alignSelf: 'center',
                      marginLeft: 2,
                    }}>+{dayTasks.length - tones.length}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main content ────────────────────────────────────────────────
function MainContent({ tasks, scale }) {
  const { t } = useTranslation();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = isoDate(today);
  const [picked, setPicked] = useState(todayIso);
  const [pickedDay, setPickedDay] = useState(null); // dla modala
  const [monthOffset, setMonthOffset] = useState(0); // -1 = poprzedni, 0 = bieżący

  const tasksByDate = useMemo(() => groupTasksByDate(tasks), [tasks]);

  if (scale === 'month') {
    const monthDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const monthName = monthDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
    const dayTasks = pickedDay ? (tasksByDate.get(pickedDay) || []) : [];

    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 20,
        }}>
          <button
            type="button"
            onClick={() => setMonthOffset(o => o - 1)}
            style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              color: 'var(--t-text-dim)', cursor: 'pointer',
              fontFamily: 'var(--t-mono)', fontSize: 12,
            }}
          >← Poprzedni</button>
          <Display size={24} style={{
            letterSpacing: '-.02em', textTransform: 'capitalize',
          }}>{monthName}</Display>
          <button
            type="button"
            onClick={() => setMonthOffset(o => o + 1)}
            style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
              color: 'var(--t-text-dim)', cursor: 'pointer',
              fontFamily: 'var(--t-mono)', fontSize: 12,
            }}
          >Następny →</button>
        </div>
        {monthOffset !== 0 && (
          <button
            type="button"
            onClick={() => setMonthOffset(0)}
            style={{
              padding: '4px 10px', borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--t-sage)',
              color: 'var(--t-sage)', cursor: 'pointer',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.16em', textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >Wróć do dziś</button>
        )}
        <MonthGrid
          year={monthDate.getFullYear()}
          month={monthDate.getMonth()}
          tasksByDate={tasksByDate}
          todayIso={todayIso}
          onPickDay={setPickedDay}
        />
        <DayEventsModal
          open={!!pickedDay}
          dateIso={pickedDay}
          tasks={dayTasks}
          onClose={() => setPickedDay(null)}
        />
      </>
    );
  }

  // Generuj 7 dni od poniedziałku tego tygodnia
  const weekStart = startOfWeekMonday(today);
  const weekDays = [0, 1, 2, 3, 4, 5, 6].map(i => addDays(weekStart, i));

  // Następne 5 dni od dzisiaj (dla list of day sections)
  const upcomingDays = [];
  for (let i = 0; i < 14; i++) {
    const d = addDays(today, i);
    const dIso = isoDate(d);
    const dayTasks = tasksByDate.get(dIso) || [];
    if (i === 0 || dayTasks.length > 0) {
      upcomingDays.push({ date: d, dateIso: dIso, tasks: dayTasks });
      if (upcomingDays.length >= 7) break;
    }
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <WeekStrip
          days={weekDays}
          todayIso={todayIso}
          tasksByDate={tasksByDate}
          onPick={setPicked}
          picked={picked}
        />
      </div>

      <div>
        {upcomingDays.map(({ dateIso, tasks: dayTasks }) => (
          <DaySection
            key={dateIso}
            dateIso={dateIso}
            tasks={dayTasks}
            isToday={dateIso === todayIso}
          />
        ))}
      </div>
    </>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ tasks }) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const week = weekNumber(new Date());
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>{formatLocalDate(new Date(), 'long')} · tydz. {week}</Meta>
      </div>
      <PageTitle title={t('calendar.title')} eyebrow={t('calendar.eyebrow_season', { year })} />
      <div style={{ padding: '0 24px' }}>
        <MainContent tasks={tasks} scale="week" />
      </div>
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ tasks }) {
  const { t } = useTranslation();
  const [scale, setScale] = useState('week');
  const year = new Date().getFullYear();
  const week = weekNumber(new Date());

  return (
    <div className="t-stagger" style={{
      padding: 'clamp(20px, 3vw, 32px) clamp(20px, 3vw, 40px) 60px',
      display: 'grid',
      gridTemplateColumns: 'minmax(240px, 300px) minmax(0, 1fr)',
      gap: 'clamp(20px, 2.5vw, 40px)',
      alignItems: 'start',
    }}>
      <div>
        <Meta style={{ marginBottom: 6 }}>
          {t('calendar.eyebrow_season', { year })} · tydz. {week}
        </Meta>
        <Display size={48} style={{
          display: 'block', marginBottom: 24, letterSpacing: '-.03em',
        }}>{t('calendar.title')}</Display>
        <CalendarSidebar scale={scale} setScale={setScale} />
      </div>
      <div>
        <MainContent tasks={tasks} scale={scale} />
      </div>
    </div>
  );
}

// ── Calendar screen root ─────────────────────────────────────────
export default function Calendar() {
  const { t } = useTranslation();
  const { isPhone, isTablet } = useViewport();
  const { data, loading, error } = useApi('/api/tasks?limit=200');

  if (error && (error.status === 401 || error.message?.includes('autoryz'))) {
    return <Layout active="cal"><ErrorState error={{ status: 401, message: error.message }} /></Layout>;
  }
  if (loading) return <Layout active="cal"><LoadingState label={t('common.loading')} /></Layout>;

  const tasks = data?.data || [];

  if (isPhone || isTablet) return <Layout active="cal"><MobileContent tasks={tasks} /></Layout>;

  const week = weekNumber(new Date());
  return (
    <Layout active="cal" topBar={<TopBar
      left={<Meta>{t('calendar.eyebrow_season', { year: new Date().getFullYear() })} · tydz. {week}</Meta>}
      right={<Meta color="sage">{t('calendar.ical_export')} →</Meta>}
    />}>
      <DesktopContent tasks={tasks} />
    </Layout>
  );
}
